import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenService } from '../lib/tokenService';
import { moodboardService } from '../lib/moodboardService';
import { RoomStyle } from '../types/moodboard';
import { ImageOptimizer, OptimizedImage } from '../lib/imageOptimizer';
import { CloudflareR2Service } from '../lib/cloudflareR2Service';
import { upstagingService } from '../lib/restyleService';
import { UpstagingGeneration } from '../types/restyle';
import { UpstagingResultViewer } from '../components/RestyleResultViewer';
import { ImageSelector } from '../components/ImageSelector';
import { UserImage, userImageService } from '../lib/userImageService';
import { userGalleryService } from '../lib/userGalleryService';
import { GenerationLoadingOverlay, LOADING_SEQUENCES } from '../components/GenerationLoadingOverlay';
import { useGenerationCosts } from '../hooks/useGenerationCosts';
import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import 'react-before-after-slider-component/dist/build.css';

export const AICleanup: React.FC = () => {
  const { user, checkTokenBalance } = useAuth();
  const { costs } = useGenerationCosts();
  const resultSectionRef = useRef<HTMLDivElement>(null);
  const operationSectionRef = useRef<HTMLDivElement>(null);
  
  // State management
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [optimizedImage, setOptimizedImage] = useState<OptimizedImage | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<RoomStyle | null>(null);
  const [customStyle, setCustomStyle] = useState<string>('');
  const [isCustomStyle, setIsCustomStyle] = useState<boolean>(false);
  const [availableStyles, setAvailableStyles] = useState<RoomStyle[]>([]);
  const [stylesLoading, setStylesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);

  const [availableRoomTypes, setAvailableRoomTypes] = useState<any[]>([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(true);
  const [selectedRoomType, setSelectedRoomType] = useState<any | null>(null);

  const [generations, setGenerations] = useState<UpstagingGeneration[]>([]);
  const [showResult, setShowResult] = useState<UpstagingGeneration | null>(null);

  // Get dynamic token cost for restyle
  const TOKENS_REQUIRED = costs.ai_upstaging || 8; // Fallback to 8 if not loaded yet

  useEffect(() => {
    if (user?.id) {
      upstagingService.getGenerations(user.id).then(setGenerations);
    }
  }, [user]);

  // Memoize token balance to prevent frequent re-renders
  const tokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);

  // Token balance is already loaded when user is set in AuthContext
  // No need to check it again here

  // Load room styles from Supabase
  useEffect(() => {
    loadRoomStyles();
    loadRoomTypes();
  }, []);

  const loadRoomTypes = async () => {
    try {
      setRoomTypesLoading(true);
      const roomTypes = await upstagingService.getRoomTypes();
      setAvailableRoomTypes(roomTypes);
    } catch (err) {
      console.error('Error loading room types:', err);
      setError('Failed to load room types. Please refresh the page.');
    } finally {
      setRoomTypesLoading(false);
    }
  };

  const loadRoomStyles = async () => {
    try {
      setStylesLoading(true);
      const styles = await moodboardService.getRoomStyles();
      // Add custom style option
      styles.push({
        id: 'custom',
        name: 'Custom Style',
        description: 'Enter your own style description',
        is_active: true,
        sort_order: 999,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setAvailableStyles(styles);
    } catch (err) {
      console.error('Error loading room styles:', err);
      setError('Failed to load available styles. Please refresh the page.');
    } finally {
      setStylesLoading(false);
    }
  };

  // Handle image selection (upload or existing)
  const handleImageSelected = async (file: File, source: 'upload' | 'existing', imageData?: UserImage) => {
    // Reset state
    setError(null);
    setSuccess(false);
    setOptimizedImage(null);

    setSelectedImage(file);
    setLoading(true);

    try {
      // Handle CORS workaround files
      let actualFile = file;
      if ((file as any).isCorsWorkaround) {
        console.log('🔄 Detected CORS workaround file, getting actual image data...');
        actualFile = await userImageService.getImageDataForWorkaround(file);
      }

      const isHeicFile = actualFile.name.toLowerCase().endsWith('.heic') || actualFile.name.toLowerCase().endsWith('.heif');

      if (isHeicFile) {
        try {
          const r2Service = CloudflareR2Service.fromEnvironment();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const uploadResult = await r2Service.uploadRawFileForProcessing(actualFile, { userId: user?.id });

          // HERE: Trigger backend processing and wait for the result
          // This is a placeholder for the actual server-side call
          // const convertedImageUrl = await triggerBackendConversion(uploadResult.key);

          // For now, we will simulate a delay and then show an error
          await new Promise(resolve => setTimeout(resolve, 5000));
          throw new Error("Server-side conversion is not yet implemented.");

        } catch (uploadError) {
          console.error('HEIC upload failed:', uploadError);
          setError('Failed to upload HEIC file for conversion. Please try again.');
          setLoading(false);
        }
        return;
      }

      // Optimize the image
      const optimized = await ImageOptimizer.optimizeImage(actualFile);
      setOptimizedImage(optimized);
      
      // Create preview URL from optimized image
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(optimized.file);

      // For new uploads, also save to user gallery
      if (source === 'upload' && user?.id) {
        try {
          await userGalleryService.uploadUserImage(actualFile, user.id);
        } catch (galleryError) {
          console.warn('Failed to save to gallery:', galleryError);
          // Continue with restyle even if gallery save fails
        }
      }

      setLoading(false);
      
      // Scroll to operations section if an existing image was selected
      if (source === 'existing' && operationSectionRef.current) {
        setTimeout(() => {
          operationSectionRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 300); // Small delay to ensure image is loaded and UI is updated
      }
    } catch (error) {
      console.error('Image processing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process image';

      if (errorMessage.startsWith('HEIC_CONVERSION_FAILED')) {
        setError(
          <span>
            HEIC/HEIF conversion is not supported in your browser. Please{' '}
            <a href="https://cloudconvert.com/heic-to-jpg" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">
              convert your image to JPG
            </a>{' '}
            or try a different browser.
          </span>
        );
      } else {
        setError(errorMessage);
      }
      
      // Fallback to original file for unsupported formats
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
      setLoading(false);
    }
  };

  const handleImageRemoved = () => {
    setSelectedImage(null);
    setOptimizedImage(null);
    setImagePreviewUrl(null);
    setError(null);
  };

  // Handle style selection
  const handleStyleSelect = (style: RoomStyle) => {
    setSelectedStyle(style);
    setIsCustomStyle(style.id === 'custom');
    if (style.id !== 'custom') {
      setCustomStyle('');
    }
    setError(null);
  };

  // Handle room type selection
  const handleRoomTypeSelect = (roomType: any) => {
    setSelectedRoomType(roomType);
    setError(null);
  };

  // Handle custom style input
  const handleCustomStyleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomStyle(event.target.value);
    setError(null);
  };

  // Get the effective style for submission
  const getEffectiveStyle = () => {
    if (isCustomStyle) {
      return {
        id: 'custom',
        name: 'Custom Style',
        description: customStyle.trim()
      };
    }
    return selectedStyle;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!user?.id || !optimizedImage) return;

    const effectiveStyle = getEffectiveStyle();
    if (!effectiveStyle) return;

    if (isCustomStyle && !customStyle.trim()) {
      setError('Please enter a custom style description.');
      return;
    }

    await checkTokenBalance(user);
    
    if (tokenBalance < TOKENS_REQUIRED) {
      setError(`Not enough tokens! You need ${TOKENS_REQUIRED} tokens but only have ${tokenBalance}.`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const processingId = `ai-upstaging-${Date.now()}`;

      const r2Service = CloudflareR2Service.fromEnvironment();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const uploadResult = await r2Service.uploadOptimizedImage(
        optimizedImage.file,
        processingId,
        user.id,
        'upstaging'
      );

      const webhookPayload = {
        user_id: user.id,
        image_url: uploadResult.url,
        style: effectiveStyle.description,
        prompt: effectiveStyle.description,
        room_type: selectedRoomType.name,
        timestamp: new Date().toISOString(),
        request_type: 'ai_upstaging',
        processing_id: processingId
      };

      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL_UPSTAGING;
      let upstagedImageUrl = '';

      if (!webhookUrl || webhookUrl === 'your_n8n_webhook_url_here') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ N8N upstaging webhook URL not configured. Using placeholder image.');
        }
        await new Promise(resolve => setTimeout(resolve, 3000));
        upstagedImageUrl = 'https://pub-c9a3716568f44448a4910db6bad42720.r2.dev/upstaged_example.jpg'; // Placeholder
      } else {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookPayload),
        });

        if (!response.ok) {
          throw new Error(`AI processing failed with status: ${response.status}`);
        }

        const result = await response.json();
        
        // Handle multiple possible n8n response structures
        if (result.return_url) {
          upstagedImageUrl = result.return_url;
        } else if (Array.isArray(result) && result[0]?.response?.body?.return_url) {
          upstagedImageUrl = result[0].response.body.return_url;
        } else {
          console.error('Unexpected n8n response format:', result);
          throw new Error('AI service returned an unexpected response format.');
        }

        if (!upstagedImageUrl) {
          throw new Error('AI service did not return the upstaged image URL.');
        }
      }
      
      const generationData = {
        user_id: user.id,
        processing_id: processingId,
        original_image_url: uploadResult.url,
        upstaged_image_url: upstagedImageUrl,
        style: effectiveStyle.name,
        prompt: effectiveStyle.description || '',
        room_type: selectedRoomType.name,
      };

      const newGeneration = await upstagingService.createGeneration(generationData);
      setGenerations([newGeneration, ...generations]);
      setShowResult(newGeneration);

      const styleDescription = isCustomStyle ? customStyle.trim() : effectiveStyle.name;
      const tokenSuccess = await tokenService.spendTokens(
        user.id,
        TOKENS_REQUIRED,
        `Restyle - ${styleDescription} style applied to image`
      );

      if (tokenSuccess) {
        await checkTokenBalance(user);
        setSuccess(true);
        
        setTimeout(() => {
          setSelectedImage(null);
          setOptimizedImage(null);
          setImagePreviewUrl(null);
          setSelectedStyle(null);
          setCustomStyle('');
          setIsCustomStyle(false);
          setSuccess(false);
          setSelectedRoomType(null);
        }, 3000);
      } else {
        setError('Failed to spend tokens. Please try again.');
      }

    } catch (err: any) {
      setError(`Error processing image: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to result section when showResult is available
  useEffect(() => {
    if (showResult && resultSectionRef.current) {
      resultSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [showResult]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            🚀 Restyle
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Upload an image and select a style to transform it with restyle
          </p>
          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            💰 Cost: {TOKENS_REQUIRED} tokens per restyle
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 mr-3">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-green-800 font-medium">
                🎉 Image successfully processed! Your upstaged image is being generated.
              </div>
            </div>
          </div>
        )}

        {/* Live Before/After Comparison */}
        {showResult && (
          <div ref={resultSectionRef} className="mb-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🚀 Live Before & After Comparison</h3>
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <ReactBeforeSliderComponent
                firstImage={{
                  imageUrl: showResult.original_image_url,
                  alt: 'Original Image'
                }}
                secondImage={{
                  imageUrl: showResult.upstaged_image_url,
                  alt: 'Upstaged Image'
                }}
                delimiterColor="#3B82F6"
                currentPercentPosition={50}
                withResizeFeel={true}
                feelsOnlyTheDelimiter={false}
                delimiterIconStyles={{
                  width: '60px',
                  height: '60px',
                  backgroundSize: 'cover',
                  borderRadius: '50%',
                  backgroundImage: 'url("/assets/renovirai-logo.png")',
                  backgroundColor: 'white',
                  border: '3px solid #3B82F6',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Drag the slider to compare before and after | Room Type: {showResult.room_type} | Style: {showResult.style}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - Image Upload */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <ImageSelector
                title="📸 Select Image"
                uploadButtonText="Upload New Image"
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                selectedImage={selectedImage}
                acceptedFormats="image/jpeg,image/jpg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
                maxSizeMB={50}
                showExisting={true}
              />

              {/* Image Preview */}
              {imagePreviewUrl && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </label>
                  <div className="border-2 border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={imagePreviewUrl}
                      alt="Preview"
                      className="max-w-full max-h-64 mx-auto rounded"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Style Selection */}
          <div className="space-y-6">
            <div ref={operationSectionRef} className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                🎨 Select Style
              </h2>
              
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-800 mb-2">Room Type</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {roomTypesLoading ? (
                    <div className="col-span-2 text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading room types...</p>
                    </div>
                  ) : (
                    availableRoomTypes.map((roomType) => (
                      <button
                        key={roomType.id}
                        onClick={() => handleRoomTypeSelect(roomType)}
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          selectedRoomType?.id === roomType.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium text-gray-900">{roomType.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Style</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {stylesLoading ? (
                    <div className="col-span-2 text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading styles...</p>
                    </div>
                  ) : (
                    availableStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => handleStyleSelect(style)}
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          selectedStyle?.id === style.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <span className="font-medium text-gray-900">{style.name}</span>
                        </div>
                        <p className="text-sm text-gray-600">{style.description}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Custom Style Input */}
              {isCustomStyle && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Style Description
                  </label>
                  <textarea
                    value={customStyle}
                    onChange={handleCustomStyleChange}
                    placeholder="Describe your desired style or artistic effect..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Be specific about the style, colors, mood, or artistic techniques you want applied
                  </p>
                </div>
              )}

            </div>

            {/* Submit Button */}
            <div className="bg-white rounded-lg shadow p-6">
              <button
                onClick={handleSubmit}
                disabled={!selectedImage || !selectedStyle || !selectedRoomType || (isCustomStyle && !customStyle.trim()) || loading || tokenBalance < TOKENS_REQUIRED}
                className={`w-full py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                  !selectedImage || !selectedStyle || !selectedRoomType || (isCustomStyle && !customStyle.trim()) || loading || tokenBalance < TOKENS_REQUIRED
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing Image...
                  </span>
                ) : (
                  `🚀 Apply ${selectedRoomType?.name || 'Room Type'} with ${selectedStyle?.name || 'Style'} (${TOKENS_REQUIRED} tokens)`
                )}
              </button>
              
              {(!selectedImage || !selectedStyle || !selectedRoomType || (isCustomStyle && !customStyle.trim())) && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {!selectedImage && 'Upload an image and '}
                  {!selectedRoomType && 'select a room type and '}
                  {!selectedStyle && 'select a style '}
                  {selectedStyle && isCustomStyle && !customStyle.trim() && 'enter custom style description '}
                  to continue
                </p>
              )}

              {tokenBalance < TOKENS_REQUIRED && (
                <p className="text-xs text-red-600 mt-2 text-center">
                  Not enough tokens! Need {TOKENS_REQUIRED} tokens.
                </p>
              )}
            </div>
          </div>
        </div>
        {generations.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Past Generations</h3>
            <div className="space-y-6">
              {generations.map((gen) => (
                <UpstagingResultViewer key={gen.id} generation={gen} />
              ))}
            </div>
          </div>
        )}
      </div>
      
      <GenerationLoadingOverlay
        isVisible={loading}
        loadingSteps={LOADING_SEQUENCES.AI_CLEANUP}
      />
    </div>
  );
}; 